import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users, Building2, IdCard, ShieldCheck, Briefcase, Sparkles, CalendarDays, DollarSign,
    TrendingUp, Activity, Loader2, AlertTriangle, ClipboardCheck, Radio, Trash2, ArrowRight,
} from "lucide-react";
import { InsightWidget } from "./InsightWidget";
import { TrialABConversionTile } from "./TrialABConversionTile";
import { StripePortalReadinessTile } from "./StripePortalReadinessTile";
import { StripeModeTile } from "./StripeModeTile";
import { useAdminContext } from "./AdminShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

// Live Payments health tile — turns green after the first live invoice.paid.
const LivePaymentsTile = () => {
    const [health, setHealth] = useState(null);
    useEffect(() => {
        let mounted = true;
        const load = () => api.get("/admin/revenue/health")
            .then((r) => mounted && setHealth(r.data))
            .catch(() => {});
        load();
        const t = setInterval(load, 60_000);
        return () => { mounted = false; clearInterval(t); };
    }, []);
    const enabled = !!health?.live_payments_enabled;
    const isLiveKey = health?.stripe_mode === "live";
    const dotClass = enabled ? "text-emerald-500 fill-emerald-500" : (isLiveKey ? "text-amber-500 fill-amber-500" : "text-slate-400 fill-slate-400");
    const label = enabled ? "Live payments enabled" : (isLiveKey ? "Awaiting first live payment" : "Stripe in test mode");
    const sub = enabled
        ? `${health.total_paid_events || 0} paid events · first ${new Date(health.first_payment_at).toLocaleDateString()}`
        : (isLiveKey ? "Keys live · webhook armed" : "Not live yet");
    return (
        <a href="/admin/revenue" className="card-soft p-5 block hover:border-emerald-500/40 transition" data-testid="live-payments-tile">
            <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${enabled ? "bg-emerald-500/10" : "bg-slate-500/10"}`}>
                    <Radio size={18} className={enabled ? "text-emerald-600 animate-pulse" : "text-slate-500"} />
                </div>
                <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <svg width="8" height="8" viewBox="0 0 8 8" className={dotClass}><circle cx="4" cy="4" r="4" /></svg>
                        Live Payments
                    </div>
                    <div className="mt-0.5 font-heading font-bold text-lg text-foreground leading-tight">{label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
                </div>
            </div>
        </a>
    );
};

const StatCard = ({ icon: Icon, label, value, sub, tone = "emerald", testId, href }) => {
    const tones = {
        emerald: "text-emerald-600 bg-emerald-500/10",
        sky: "text-sky-600 bg-sky-500/10",
        amber: "text-amber-600 bg-amber-500/10",
        rose: "text-rose-600 bg-rose-500/10",
        violet: "text-violet-600 bg-violet-500/10",
        slate: "text-slate-600 bg-slate-500/10",
    };
    const inner = (
        <div className="flex items-start gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
                <Icon size={18} />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    {label}
                    {href && <ArrowRight size={10} className="opacity-0 group-hover:opacity-100 transition text-emerald-600" />}
                </div>
                <div className="mt-0.5 font-heading font-bold text-2xl text-foreground leading-tight">
                    {value === null || value === undefined ? "—" : value}
                </div>
                {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
            </div>
        </div>
    );
    if (href) {
        return (
            <Link
                to={href}
                className="card-soft p-5 group block hover:border-emerald-500/40 hover:shadow-md transition cursor-pointer"
                data-testid={testId}
            >
                {inner}
            </Link>
        );
    }
    return (
        <div className="card-soft p-5" data-testid={testId}>{inner}</div>
    );
};

const Section = ({ title, children, sub }) => (
    <section className="mt-8">
        <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-heading font-semibold text-lg text-foreground">{title}</h2>
            {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
        </div>
        {children}
    </section>
);

export default function ExecutiveDashboard() {
    const { me } = useAdminContext();
    const isMasterAdmin = (me?.email || "").toLowerCase().trim() === "admin@simonaratalent.us";
    // Master admin's Talent Passports tile lands on the full directory view
    // (list + filters + Nudge / Resume / CSV) instead of the email-only lookup
    // page — makes the "13 stale test passports" easy to see and delete.
    const passportsHref = isMasterAdmin ? "/admin/all-professionals?filter=with_passport" : "/admin/passport-lookup";
    const verifiedHref = isMasterAdmin ? "/admin/all-professionals?filter=id_verified" : "/admin/passport-lookup?filter=verified";

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/dashboard")
            .then((r) => setStats(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;
    if (!stats) return <div className="card-soft p-8 text-center text-muted-foreground">Unable to load dashboard.</div>;

    const ph = stats.platform_health;
    const g = stats.growth;
    const ai = stats.ai_usage;

    return (
        <div data-testid="admin-executive-dashboard">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Executive Dashboard
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Live snapshot of Simonara Talent Solutions.</p>
                </div>
                {ai.briefs_pending > 0 && (
                    <a href="/admin/reviews" className="btn-green text-sm inline-flex items-center gap-2" data-testid="dash-review-cta">
                        <AlertTriangle size={14} /> {ai.briefs_pending} brief{ai.briefs_pending === 1 ? "" : "s"} awaiting review
                    </a>
                )}
            </div>

            <div className="mt-6">
                <InsightWidget />
            </div>

            <Section title="Billing readiness" sub="Live Stripe mode, customer-portal toggles, and trial outcomes at a glance.">
                <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
                    <div className="lg:col-span-2"><StripeModeTile /></div>
                    <div className="lg:col-span-2"><StripePortalReadinessTile /></div>
                    <div className="lg:col-span-2"><TrialABConversionTile /></div>
                </div>
            </Section>

            <Section title="Platform Health" sub="Total counts across the platform.">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={Users} tone="emerald" label="Professionals" value={ph.total_professionals} testId="dash-stat-professionals" href="/admin/users?role=professional" />
                    <StatCard icon={Building2} tone="sky" label="Employers" value={ph.total_employers} testId="dash-stat-employers" href="/admin/users?role=employer" />
                    <StatCard icon={IdCard} tone="violet" label="Talent Passports" value={ph.total_passports} sub={`${ph.passports_with_resume ?? ph.verified_passports} with resume`} testId="dash-stat-passports" href={passportsHref} />
                    <StatCard icon={ShieldCheck} tone="amber" label="Verified Passports" value={ph.verified_passports} sub="ID-verified via Stripe" testId="dash-stat-verified" href={verifiedHref} />
                    <StatCard icon={Briefcase} tone="emerald" label="Active Jobs" value={ph.active_jobs} sub={`${ph.total_jobs} total`} testId="dash-stat-jobs" href="/admin/jobs" />
                    <StatCard icon={Sparkles} tone="violet" label="AI Matches Today" value={ph.ai_matches_today} testId="dash-stat-ai-today" href="/admin/analytics/ai" />
                    <StatCard icon={CalendarDays} tone="sky" label="Interviews Scheduled" value={ph.interviews_scheduled} testId="dash-stat-interviews" href="/admin/analytics-hub" />
                    <LivePaymentsTile />
                </div>
            </Section>

            <Section title="Growth" sub="New signups + active users.">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <StatCard icon={TrendingUp} tone="emerald" label="New today" value={g.new_users_today} href="/admin/users" />
                    <StatCard icon={TrendingUp} tone="sky" label="New employers (7d)" value={g.new_employers_week} href="/admin/users?role=employer" />
                    <StatCard icon={Activity} tone="violet" label="DAU" value={g.dau} sub="Daily active" href="/admin/users" />
                    <StatCard icon={Activity} tone="amber" label="WAU" value={g.wau} sub="Weekly active" href="/admin/users" />
                    <StatCard icon={Activity} tone="rose" label="MAU" value={g.mau} sub="Monthly active" href="/admin/users" />
                </div>
            </Section>

            <Section title="AI Usage" sub="How Simonara AI is being used.">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={Sparkles} tone="violet" label="Hiring Briefs (30d)" value={ai.hiring_briefs_month} testId="dash-stat-briefs-month" href="/admin/reviews" />
                    <StatCard icon={ClipboardCheck} tone="emerald" label="Ready Briefs" value={ai.briefs_ready} href="/admin/reviews" />
                    <StatCard icon={AlertTriangle} tone="amber" label="Pending Review" value={ai.briefs_pending} testId="dash-stat-briefs-pending" href="/admin/reviews" />
                    <StatCard icon={AlertTriangle} tone="rose" label="Declined" value={ai.briefs_declined} href="/admin/reviews" />
                    <StatCard icon={IdCard} tone="sky" label="Passport Requests" value={ai.passport_requests_total} sub={`${ai.passport_requests_consented} consented`} href="/admin/requests" />
                    <StatCard icon={Sparkles} tone="emerald" label="Marketplace Reach-outs" value={ai.marketplace_reachouts} href="/admin/requests" />
                    <StatCard icon={Sparkles} tone="violet" label="Resume Reviews" value={ai.resume_optimizations} sub="AI-generated" href="/admin/resumes" />
                </div>
            </Section>

            <Section title="Housekeeping" sub="Clean up test data + moderate content.">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Link to="/admin/data-purge" className="card-soft p-5 group hover:border-rose-500/40 hover:shadow-md transition" data-testid="dash-link-data-purge">
                        <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-xl bg-rose-500/10 text-rose-600 flex items-center justify-center"><Trash2 size={18} /></div>
                            <div className="min-w-0">
                                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Data Purge</div>
                                <div className="mt-0.5 font-heading font-bold text-base text-foreground leading-tight">Wipe test users by email pattern</div>
                                <div className="text-xs text-muted-foreground mt-0.5">Dry-run preview before commit · audit-logged</div>
                            </div>
                        </div>
                    </Link>
                    <Link to="/admin/jobs" className="card-soft p-5 group hover:border-emerald-500/40 hover:shadow-md transition" data-testid="dash-link-jobs-manage">
                        <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center"><Briefcase size={18} /></div>
                            <div className="min-w-0">
                                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Manage Jobs</div>
                                <div className="mt-0.5 font-heading font-bold text-base text-foreground leading-tight">Edit or delete any job</div>
                                <div className="text-xs text-muted-foreground mt-0.5">Sort by newest · delete test postings</div>
                            </div>
                        </div>
                    </Link>
                    <Link to="/admin/users?role=professional" className="card-soft p-5 group hover:border-sky-500/40 hover:shadow-md transition" data-testid="dash-link-users-manage">
                        <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center"><Users size={18} /></div>
                            <div className="min-w-0">
                                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Manage Users</div>
                                <div className="mt-0.5 font-heading font-bold text-base text-foreground leading-tight">Nudge, reset, suspend, delete</div>
                                <div className="text-xs text-muted-foreground mt-0.5">See last-seen · send passport reminders</div>
                            </div>
                        </div>
                    </Link>
                </div>
            </Section>
        </div>
    );
}
